/**
 * Phone number formatting utilities
 */

import { FormatRegistry } from "./registry";
import type { FieldValue, FormattedValue, PhoneFormat } from "./types";

/**
 * Format US phone number
 */
export const formatUSPhone = (phone: string | null | undefined): string | null => {
  if (!phone) return null;

  // Remove all non-digits
  const cleaned = phone.replace(/\D/g, "");

  // Check if it's a valid US phone number (10 or 11 digits)
  if (cleaned.length === 10) {
    return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "($1) $2-$3");
  } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return cleaned.replace(/1(\d{3})(\d{3})(\d{4})/, "+1 ($1) $2-$3");
  }

  // Return original if not a valid format
  return phone;
};

/**
 * Format international phone number
 */
export const formatInternationalPhone = (phone: string | null | undefined): string | null => {
  if (!phone) return null;

  // Remove all non-digits except +
  const cleaned = phone.replace(/[^\d+]/g, "");

  // If it starts with +, format as international
  if (cleaned.startsWith("+")) {
    // Try to format common patterns
    if (cleaned.length === 12 && cleaned.startsWith("+1")) {
      // US/Canada
      return cleaned.replace(/\+1(\d{3})(\d{3})(\d{4})/, "+1 ($1) $2-$3");
    } else if (cleaned.length === 13 && cleaned.startsWith("+44")) {
      // UK
      return cleaned.replace(/\+44(\d{2})(\d{4})(\d{4})/, "+44 $1 $2 $3");
    }
    // Add spaces every 3-4 digits for other countries
    return cleaned.replace(/(\+\d{1,3})(\d{3,4})(\d{3,4})(\d{3,4})?/, "$1 $2 $3 $4").trim();
  }

  // Default to US format if no country code
  return formatUSPhone(phone);
};

/**
 * Format phone number with basic formatting
 */
export const formatPhoneNumber = (phone: string | null | undefined): string | null => {
  if (!phone) return null;

  // Try to detect if it's international
  if (phone.startsWith("+") || phone.length > 11) {
    return formatInternationalPhone(phone);
  }

  return formatUSPhone(phone);
};

/**
 * Format phone according to PhoneFormat specification
 */
export const formatPhone = (value: FieldValue, format: PhoneFormat): FormattedValue => {
  if (format.hideWhenNull && (value === null || value === undefined)) return null;
  if (value == null) return format.nullText ?? null;

  const phoneStr = String(value);

  if (format.international) {
    return formatInternationalPhone(phoneStr);
  }

  // Handle specific country formats if needed
  switch (format.country) {
    case "US":
    case "CA":
      return formatUSPhone(phoneStr);
    default:
      return formatPhoneNumber(phoneStr);
  }
};

// Register formatters
FormatRegistry.register("phoneNumber", (value) => {
  if (typeof value === "string") {
    return formatPhoneNumber(value);
  }
  return null;
});

FormatRegistry.register("usPhone", (value) => {
  if (typeof value === "string") {
    return formatUSPhone(value);
  }
  return null;
});

FormatRegistry.register("internationalPhone", (value) => {
  if (typeof value === "string") {
    return formatInternationalPhone(value);
  }
  return null;
});
